// ─── Admin Complaints Tab — الشكاوي والملاحظات ───
// Lists all customer complaints with filter (open/resolved/all).
// Shows customer name + market + iPad ID + phone. Admin can reply,
// mark resolved, or delete. Newest first.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertCircle,
  Loader2,
  CheckCircle2,
  Clock3,
  Trash2,
  Reply,
  Phone,
  Store,
  IdCard,
  User,
  Search,
  MessageCircle,
  Send,
} from "lucide-react";
import { smartMatch } from "../../utils/arabicSearch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { id: "open", label: "قيد المراجعة" },
  { id: "resolved", label: "تم الحل" },
  { id: "all", label: "الكل" },
];

const ComplaintsTab = () => {
  const [filter, setFilter] = useState("open");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyDraft, setReplyDraft] = useState({}); // id → text
  const [busyId, setBusyId] = useState(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await axios.get(`${API}/admin/complaints`, { params });
      setRows(data || []);
    } catch (err) {
      toast.error("فشل تحميل الشكاوي");
    } finally {
      setLoading(false);
    }
  };

  // Mark all visible as read whenever the tab opens (drives the badge to 0).
  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Bulk mark unread → read on mount
  useEffect(() => {
    const unread = rows.filter((c) => !c.read);
    if (unread.length === 0) return;
    Promise.all(
      unread.map((c) => axios.patch(`${API}/admin/complaints/${c.id}`, { read: true }).catch(() => null))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    return rows.filter((c) => smartMatch(
      `${c.customer_name} ${c.customer_phone} ${c.market_name} ${c.customer_id_no} ${c.message}`,
      q
    ));
  }, [rows, q]);

  const resolve = async (id) => {
    setBusyId(id);
    try {
      const reply = (replyDraft[id] || "").trim();
      await axios.patch(`${API}/admin/complaints/${id}`, { status: "resolved", admin_reply: reply });
      toast.success("تم وضع علامة 'محلولة'");
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally {
      setBusyId(null);
    }
  };

  const reply = async (id) => {
    const text = (replyDraft[id] || "").trim();
    if (text.length < 1) { toast.error("اكتب رداً"); return; }
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/complaints/${id}`, { admin_reply: text });
      toast.success("تمّ حفظ ردّك");
      setReplyDraft((d) => { const n = { ...d }; delete n[id]; return n; });
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally {
      setBusyId(null);
    }
  };

  const del = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/complaints/${id}`);
      toast.success("تم الحذف");
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="complaints-tab" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#E85D45] to-[#dc2626] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <AlertCircle className="w-5 h-5" />
          <h3 className="font-bold text-lg">الشكاوي والملاحظات من الزبائن</h3>
        </div>
        <p className="text-xs opacity-90">جميع الرسائل المُرسَلة من الزبائن — اكتب ردّاً واضغط "حلّ" لإغلاق الشكوى.</p>
      </div>

      {/* Filter + search */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 bg-[#F3F6F4] rounded-full p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              data-testid={`complaints-filter-${f.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                filter === f.id ? "bg-[#E85D45] text-white" : "text-[#5C6E64] hover:bg-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6E64]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="complaints-search"
            placeholder="ابحث باسم الزبون / الماركت / الايباد / الهاتف..."
            className="w-full pr-8 pl-3 py-2 rounded-full border border-[#E4EBE6] text-xs"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#E85D45]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center">
          <MessageCircle className="w-10 h-10 mx-auto text-[#5C6E64] mb-2" />
          <p className="text-sm text-[#5C6E64]">لا توجد شكاوي ضمن هذا التصنيف.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="complaints-list">
          {filtered.map((c) => {
            const resolved = c.status === "resolved";
            return (
              <div
                key={c.id}
                data-testid={`complaint-card-${c.id}`}
                className={`bg-white rounded-2xl p-3 border-2 ${resolved ? "border-[#7ED321]/40" : "border-[#E85D45]/40"}`}
              >
                {/* Customer header */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-black text-[#111814] flex items-center gap-1">
                      <User className="w-3.5 h-3.5 text-[#E85D45]" />
                      {c.customer_name}
                    </span>
                    {c.market_name && (
                      <span className="bg-[#F0F9FF] text-[#1E3A5F] rounded-full px-2 py-0.5 flex items-center gap-1">
                        <Store className="w-3 h-3" /> {c.market_name}
                      </span>
                    )}
                    {c.customer_id_no && (
                      <span className="bg-[#F5F3FF] text-[#7C3AED] rounded-full px-2 py-0.5 flex items-center gap-1" dir="ltr">
                        <IdCard className="w-3 h-3" /> {c.customer_id_no}
                      </span>
                    )}
                    {c.customer_phone && (
                      <a
                        href={`tel:${c.customer_phone}`}
                        className="bg-[#DCFCE7] text-[#15803d] hover:underline rounded-full px-2 py-0.5 flex items-center gap-1"
                        dir="ltr"
                      >
                        <Phone className="w-3 h-3" /> {c.customer_phone}
                      </a>
                    )}
                  </div>
                  {resolved ? (
                    <span className="bg-[#DCFCE7] text-[#15803d] text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1 shrink-0">
                      <CheckCircle2 className="w-3 h-3" /> تم الحل
                    </span>
                  ) : (
                    <span className="bg-[#FEF3C7] text-[#92400e] text-[10px] font-bold rounded-full px-2 py-0.5 flex items-center gap-1 shrink-0">
                      <Clock3 className="w-3 h-3" /> قيد المراجعة
                    </span>
                  )}
                </div>

                {/* Message */}
                <div className="bg-[#FEF2F2] rounded-xl p-2.5 mb-2 border-r-4 border-[#E85D45]">
                  <p className="text-sm text-[#111814] whitespace-pre-wrap">{c.message}</p>
                  <p className="text-[9px] text-[#5C6E64] mt-1" dir="ltr">
                    {new Date(c.created_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}
                  </p>
                </div>

                {/* Existing admin reply */}
                {c.admin_reply && (
                  <div className="bg-[#DCFCE7] rounded-xl p-2.5 mb-2 border-r-4 border-[#15803d]">
                    <p className="text-[10px] font-bold text-[#15803d] mb-0.5">ردّك:</p>
                    <p className="text-sm text-[#111814] whitespace-pre-wrap">{c.admin_reply}</p>
                  </div>
                )}

                {/* Reply + actions */}
                {!resolved && (
                  <div className="space-y-2">
                    <textarea
                      value={replyDraft[c.id] || c.admin_reply || ""}
                      onChange={(e) => setReplyDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                      placeholder="اكتب ردّاً للزبون (اختياري — سيظهر له في صفحة الشكاوي)..."
                      data-testid={`complaint-reply-${c.id}`}
                      rows={2}
                      className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] text-xs resize-none focus:border-[#E85D45] focus:outline-none"
                    />
                    <div className="flex gap-1.5 flex-wrap">
                      <button
                        onClick={() => reply(c.id)}
                        disabled={busyId === c.id}
                        data-testid={`complaint-reply-save-${c.id}`}
                        className="bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 text-white text-xs font-bold rounded-full px-3 py-1.5 flex items-center gap-1"
                      >
                        <Send className="w-3 h-3" /> حفظ الردّ
                      </button>
                      <button
                        onClick={() => resolve(c.id)}
                        disabled={busyId === c.id}
                        data-testid={`complaint-resolve-${c.id}`}
                        className="bg-[#15803d] hover:bg-[#0e6b30] disabled:opacity-50 text-white text-xs font-bold rounded-full px-3 py-1.5 flex items-center gap-1"
                      >
                        <CheckCircle2 className="w-3 h-3" /> حلّ وأغلق
                      </button>
                      <button
                        onClick={() => del(c.id)}
                        disabled={busyId === c.id}
                        data-testid={`complaint-delete-${c.id}`}
                        className="bg-[#FEE2E2] hover:bg-[#fecaca] text-[#dc2626] text-xs font-bold rounded-full px-3 py-1.5 flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> حذف
                      </button>
                    </div>
                  </div>
                )}
                {resolved && (
                  <button
                    onClick={() => del(c.id)}
                    disabled={busyId === c.id}
                    data-testid={`complaint-delete-${c.id}`}
                    className="bg-[#FEE2E2] hover:bg-[#fecaca] text-[#dc2626] text-xs font-bold rounded-full px-3 py-1.5 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> حذف
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ComplaintsTab;
